import * as React from 'react';
import { Window, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import * as svgIcons from "@progress/kendo-svg-icons";
import { Chat, MessageModel, ChatMessageSendEvent, ChatMessageProps, ChatMessageBoxProps } from '@progress/kendo-react-conversational-ui';
import { CommentModel, CommentService } from 'services/CommentService';
import { UserService } from 'services/UserService';
import { t } from "i18next";
import { Trans } from "react-i18next";
import { MentionInput } from "./MentionInputComponent";
import config from 'contexts/ConfigContext';
import { ODataProvider } from 'contexts/ODataContext';

interface ChatMessage extends MessageModel {
  id: number;
  selectionIndex: number;
}

interface Comment {
  id: number;
  content: string;
  annotationId: number;
  documentId: number;
  firstName: string;
  lastName: string;
  createdBy: string;
  lastUpdatedDate: string;
}

interface UserModel {
  UserId: number;
  Username: string;
}

interface CommentSectionProps {
  onClose: () => void;
  existingComments: Comment[];
  typeId: number; //0 for Annotation, 1 for Document
  annotationIdProp: number;
  documentIdProp: number;
}

interface DeletableMessageProps extends ChatMessageProps {
  onDelete: (msg: ChatMessage) => void;
  currentUserId: number | null;
}

interface DeleteCommentDialogProps {
  onConfirm: () => void;
  onCancel: () => void;
};

export function CommentSection({onClose, existingComments, typeId, annotationIdProp, documentIdProp}: CommentSectionProps){
  const zIndexDimmer = 10100;
  const userService = new UserService();
  const commentService = new CommentService();
  const annotationId = annotationIdProp;
  const documentId = documentIdProp;
  const contentMaxLength = 280;
  const [userId, setUserId] = React.useState<number | null>(null);
  const [messageToDelete, setMessageToDelete] = React.useState<ChatMessage | null>(null);
  const [showDialog, setShowDialog] = React.useState(false);
  const [zIndex, setzIndex] = React.useState(zIndexDimmer);
  const mentionIdsRef = React.useRef<number[]>([]);
  const userAuthor = { id: userId, name: `${t("dataLabeling.labeling.comments.author")}` };
  const userApiUrl = config.API_URL + "/odata/ProjectUsers/";

  //Get current user id
  React.useEffect(() => {
    const fetchUserId = async () => {
      try {
        const user: UserModel = await userService.GetUser();
        setUserId(user.UserId);
      } catch (error) {
        setUserId(null);
      }
    };
  
    fetchUserId();
  }, []);

  //Delete a comment
  const handleDelete = (message: ChatMessage) => {
    setzIndex(900);
    setMessageToDelete(message);
    setShowDialog(true);
  };
  
  const confirmDelete = async () => {
    if (!messageToDelete) return;
  
    await commentService.DeleteComment(typeId, annotationId, messageToDelete.id);
    setComments(prev => prev.filter(m => m.id !== messageToDelete.id));
  
    setzIndex(zIndexDimmer);
    setShowDialog(false);
    setMessageToDelete(null);
  };
  
  const cancelDelete = async () => {
    setzIndex(zIndexDimmer);
    setShowDialog(false);
    setMessageToDelete(null);
  };

  //Get All names and last edited update date of each comment
  const initialComments: ChatMessage[] = existingComments.map((c) => {
    const displayName =
      c.firstName || c.lastName
        ? `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim()
        : `${t("dataLabeling.labeling.comments.unknown")}: ${c.createdBy}`;

    return {
      id: c.id,
      author: {
        id: c.createdBy,
        name: displayName,
      },
      text: c.content,
      timestamp: new Date(c.lastUpdatedDate),
      selectionIndex: c.id
    };
  });

  const [comments, setComments] = React.useState<ChatMessage[]>(initialComments);
  //Sort comments by date
  const sortedComments = [...comments].sort(
    (a, b) => a.timestamp!.getTime() - b.timestamp!.getTime()
  );

  //Method to add a new comment
  const addNewComment = async (event: ChatMessageSendEvent) => {
    const userComment = event.message;

    const newComment: CommentModel = {
      CommentId: 0, // WIll be created by BE
      UserMentions: mentionIdsRef.current,
      TypeId: typeId,
      AnnotationId: annotationId ?? null,
      DocumentId: documentId ?? null,
      CommentContent: userComment.text ?? ""
    };

    // Reset mentions ref
    mentionIdsRef.current = [];
    
    const result = await commentService.AddComment(newComment);
    if (result) {
      userAuthor.name = result.FirstName + " " + result.LastName;
      const newMessage: ChatMessage = {
        ...userComment,
        id: result.CommentId,
        selectionIndex: result.CommentId
      };
      setComments((prev) => [...prev, newMessage]);

      setTimeout(() => {
        const container = document.querySelector('.k-message-list') as HTMLElement;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 0);
    }
  };

  const DeletableMessage: React.FC<DeletableMessageProps> = ({
    item,
    tabIndex,
    onDelete,
    currentUserId
  }) => {
    const [hover, setHover] = React.useState(false);
    const [trashHover, setTrashHover] = React.useState(false);
    const isOwnMessage = item.author.id === currentUserId;
  
    return (
      <>
      <div
      className={`message-container ${hover && isOwnMessage ? 'show-gap' : ''}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      tabIndex={tabIndex}
    >
      <div
      className={`message-bubble 
        ${isOwnMessage ? 'own' : ''} 
        ${hover && isOwnMessage ? 'translate' : ''}`}
      >
        {item.text}
      </div>
  
      {hover && isOwnMessage && (
        <Button
          onClick={() => onDelete(item as ChatMessage)}
          onMouseEnter={() => setTrashHover(true)}
          onMouseLeave={() => setTrashHover(false)}
          className={'delete-button'}
          title={t("dataLabeling.labeling.comments.delete")}
        >
          <SvgIcon icon={svgIcons.trashIcon} style={{color: trashHover ? 'red' : '#888', transition: 'color 0.2s ease'}} />
        </Button>
      )}
    </div>
    {showDialog && (<DeleteCommentDialog onConfirm={confirmDelete} onCancel={cancelDelete}/> )}
    </>
    );
  };

  const CustomMessageBox = (props: ChatMessageBoxProps) => {
    const [messageText, setMessageText] = React.useState('');
    
    //Send a comment
    const handleSend = () => {
      if (!messageText.trim()) return;
      
      const commentEvent = {
        message: {
          author: userAuthor,
          text: messageText,
          timestamp: new Date(),
        }
      };
  
      addNewComment(commentEvent as ChatMessageSendEvent);

      // Reset input text
      setMessageText('');
    };

    //Handle Input Change
    const handleMessageChange = (text: string, ids: number[]) => {
      setMessageText(text);
      mentionIdsRef.current = ids;
    };

    return (
      <div
        className="k-message-box k-input input-box"
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          padding: '0.5rem',
          borderTop: '1px solid #e2e2e2',
          backgroundColor: '#fff',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <ODataProvider baseUrl={userApiUrl} loadingContent=".k-grid-content">
          <MentionInput
            value={messageText}
            currentUserId={userAuthor.id}
            onChange={handleMessageChange}
            placeholder={t("dataLabeling.labeling.comments.placeHolder")}
            maxLength={contentMaxLength}
            onSend={handleSend}
          />
        </ODataProvider>
  
        {React.cloneElement(props.sendButton, {
          onClick: handleSend,
        })}
      </div>
    );
  };
  
  const DeleteCommentDialog: React.FC<DeleteCommentDialogProps> = ({ onConfirm, onCancel }) => (
    <Window 
      title={t("dataLabeling.labeling.comments.deleteTitle")}
      className={'delete-window'}
      onClose={onCancel} 
      resizable={false} 
      draggable={false}
      maximizeButton={() => null} 
      minimizeButton={() => null}
      doubleClickStageChange={false} >
        <p style={{ margin: "20px 0" }}><Trans>dataLabeling.labeling.comments.deleteConfirmationMessage</Trans></p>
        <DialogActionsBar>
          <Button onClick={onCancel}><Trans>dataLabeling.labeling.comments.cancel</Trans></Button>
          <Button themeColor="error" onClick={onConfirm}>
            <Trans>dataLabeling.labeling.comments.delete</Trans>
          </Button>
        </DialogActionsBar>
    </Window>
  );
    
  return(
    <div className="h-100">
      <Window 
        title={ typeId === 0 ? `${t("dataLabeling.labeling.comments.annotationTitle")}` : `${t("dataLabeling.labeling.comments.documentTitle")}`}
        onClose={onClose} 
        modal={true} 
        maximizeButton={() => null} 
        minimizeButton={() => null} 
        doubleClickStageChange={false}
        resizable={false} 
        className={'comments-window'}
        style={{ zIndex: zIndex }}>
          <Chat
            user={userAuthor}
            messages={sortedComments}
            onMessageSend={addNewComment}
            placeholder={t("dataLabeling.labeling.comments.placeHolder")}
            width="100%"
            message={(props) => (
              <DeletableMessage
                {...props}
                onDelete={handleDelete}
                currentUserId={userAuthor.id}
              />
            )}
            messageBox={CustomMessageBox}
          />
      </Window>
    </div>
  );
};